// Reassurance while booking (GET /api/public/booking-trust): trust bar on top of the Beds24 frame, guarantees + after-booking timeline below it.
import React, { useEffect, useState } from 'react';
import { BadgeCheck, Building2, CreditCard, FileText, Leaf, Lock, Mail, MapPin, MessageCircle, Star, Tag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const L = { en: 'en', me: 'sr', sq: 'sq', de: 'de', ru: 'ru', pl: 'pl', hu: 'hu' };
let shared = null;

export const useBookingTrust = () => {
  const [data, setData] = useState(shared);
  useEffect(() => {
    if (shared) return undefined;
    let alive = true;
    fetch(`${API}/api/public/booking-trust`).then((r) => r.json()).then((d) => { shared = d; if (alive) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return data;
};

const Chip = ({ icon: Icon, children, testId, href, tone = '' }) => {
  const cls = `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${tone || 'border-white/15 bg-white/5 text-white/85'}`;
  return href ? <a href={href} target="_blank" rel="noopener noreferrer" className={`${cls} hover:bg-white/10 transition-colors`} data-testid={testId}><Icon className="w-3.5 h-3.5 shrink-0" />{children}</a>
    : <span className={cls} data-testid={testId}><Icon className="w-3.5 h-3.5 shrink-0" />{children}</span>;
};

export const BookingTrustBar = ({ lang, bookingUrl }) => {
  const d = useBookingTrust();
  if (!d) return null;
  const k = L[lang] || 'en';
  const t = d.texts[k] || d.texts.en;
  const online = d.reception?.online;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="booking-trust-bar">
      <Chip icon={Lock} testId="trust-secure">{t.secure}</Chip>
      {d.pill?.[k] && <Chip icon={Star} href={bookingUrl} testId="trust-score" tone="border-[#2f6fd1]/60 bg-[#003b95] text-white font-semibold">{d.pill[k]}</Chip>}
      <Chip icon={CreditCard} testId="trust-payment">{t.payment}</Chip>
      <Chip icon={FileText} testId="trust-cancel">{t.cancel}</Chip>
      {t.h_family && <Chip icon={Leaf} testId="trust-halal">{t.h_family} · {t.h_alcohol}</Chip>}
      <Chip icon={MessageCircle} testId="trust-reception" tone={online ? 'border-emerald-400/50 bg-emerald-500/15 text-emerald-100' : 'border-white/15 bg-white/5 text-white/70'}>
        <span className={`w-2 h-2 rounded-full ${online ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'}`} data-testid="trust-reception-dot" />{t.reception}
      </Chip>
      <Chip icon={Building2} testId="trust-legal">{d.legal?.name} · {d.legal?.address} · {t.legal}</Chip>
    </div>
  );
};

const logClaim = (context, lang) => {
  let vid = '';
  try {
    vid = localStorage.getItem('lrn_vid') || '';
    if (!/^[a-z0-9]{8,40}$/.test(vid)) { vid = (Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/[^a-z0-9]/g, '').slice(0, 24); localStorage.setItem('lrn_vid', vid); }
  } catch (e) { /* storage blocked */ }
  fetch(`${API}/api/public/price-match/claim`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
    body: JSON.stringify({ vid, room: context?.room || '', dates: context?.dates || '', lang, source: 'website', page: window.location.href.slice(0, 300) }) }).catch(() => {});
};

const ICONS_G = [BadgeCheck, CreditCard, FileText];
const ICONS_T = [Mail, MessageCircle, MapPin];

export const BookingTrustCards = ({ lang, context }) => {
  const d = useBookingTrust();
  if (!d) return null;
  const t = d.texts[L[lang] || 'en'] || d.texts.en;
  const guarantees = [t.g1, t.g2, t.g3].map((txt, i) => {
    const Icon = ICONS_G[i];
    return <li key={i} className="flex gap-3 text-sm text-white/85" data-testid={`trust-guarantee-${i}`}><Icon className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" /><span>{txt}</span></li>;
  });
  const steps = [t.t1, t.t2, t.t3].map((txt, i) => {
    const Icon = ICONS_T[i];
    return (
      <li key={i} className="relative flex gap-3 text-sm text-white/85" data-testid={`trust-step-${i}`}>
        <span className="relative z-10 w-7 h-7 rounded-full bg-orange-500 text-white font-bold text-xs flex items-center justify-center shrink-0">{i + 1}</span>
        <span className="pt-1 flex items-start gap-2"><Icon className="w-4 h-4 text-orange-300 shrink-0 mt-0.5" />{txt}</span>
      </li>
    );
  });
  const wa = `https://wa.me/${String(d.whatsapp || '').replace(/\D/g, '')}?text=${encodeURIComponent(t.pm_msg.replace('{room}', context?.room || '[room]').replace('{dates}', context?.dates || '[dates]'))}`;
  return (
    <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="booking-trust-cards">
      <section className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5" data-testid="booking-guarantees">
        <h3 className="text-white font-bold text-base mb-3 flex items-center gap-2"><BadgeCheck className="w-5 h-5 text-orange-400" />{t.g_title}</h3>
        <ul className="space-y-2.5">{guarantees}</ul>
        <div className="mt-4 rounded-xl border border-orange-400/30 bg-orange-500/10 p-3" data-testid="booking-price-match">
          <p className="text-sm font-bold text-white flex items-center gap-2"><Tag className="w-4 h-4 text-orange-300" />{t.pm_title}</p>
          <p className="text-xs text-white/75 mt-1 leading-relaxed">{t.pm_text}</p>
          <a href={wa} target="_blank" rel="noopener noreferrer" onClick={() => logClaim(context, lang)} className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#25d366] text-white text-xs font-semibold px-3 py-1.5 hover:brightness-110 transition" data-testid="booking-price-match-wa"><MessageCircle className="w-3.5 h-3.5" />{t.pm_cta}</a>
        </div>
      </section>
      <section className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5" data-testid="booking-timeline">
        <h3 className="text-white font-bold text-base mb-3 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-orange-400" />{t.t_title}</h3>
        <ol className="space-y-3 relative before:absolute before:left-[13px] before:top-3 before:bottom-3 before:w-px before:bg-orange-500/40">{steps}</ol>
      </section>
    </div>
  );
};
